/* eslint-disable */
/*!
   _____ ______ __  __ _    _  _____ __  __ ______ _____  
  / ____|  ____|  \/  | |  | |/ ____|  \/  |  ____|  __ \ 
 | |  __| |__  | \  / | |__| | (___ | \  / | |__  | |  | |
 | | |_ |  __| | |\/| |  __  |\___ \| |\/| |  __| | |  | |
 | |__| | |____| |  | | |  | |____) | |  | | |____| |__| |
  \_____|______|_|  |_|_|  |_|_____/|_|  |_|______|_____/                                                                                                                                                                                                                                                                                                   
=========================================================
* GEMHSMED UI - v2.0.0
=========================================================

* Copyright 2024 GEMHS (https://gemhs.net/)

* Designed and Coded by GEMHS

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software. 

*/

// Chakra imports
import {
    Box,
    Icon,
    IconButton,
    Spinner,
    SimpleGrid,
    useDisclosure,
    useColorModeValue,
    Input,
    InputGroup,
    InputLeftElement,
    Button,
    Text,
    Flex,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Select,
} from "@chakra-ui/react";
import MiniStatistics from "components/card/MiniStatistics";
import IconBox from "components/icons/IconBox";
import React, { useState, useEffect, useMemo } from "react";
import {
    MdPerson,
} from "react-icons/md";
import { useParams, useNavigate } from "react-router-dom";

import {
    useGetCurrentUserQuery,
    useGetUsersQuery,
    useGetAllVisitsQuery,
    useSearchVisitsQuery,
} from "store";
import moment from "moment";
import PageTitle from "components/title";
import { InfoIcon, SearchIcon } from "@chakra-ui/icons";

function PaginatedTable({ columns, initialPageSize = 10 }) {
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(initialPageSize);
    const [searchQuery, setSearchQuery] = useState("");

        const [triggeredSearchQuery, setTriggeredSearchQuery] = useState(""); // Store triggered search query
    // const [searchTimeout, setSearchTimeout] = useState(null); // Store timeout ID

    const { data: visits, isFetching, error } = triggeredSearchQuery
            ? useSearchVisitsQuery({
                  page: pageIndex + 1,
                  pageSize,
                  search: triggeredSearchQuery,
              })
            : useGetAllVisitsQuery(
        { page: pageIndex + 1, pageSize },
        { keepPreviousData: true }
    );

    // Safely handle undefined visits and default to last page as 0
    const lastPage =
        visits && visits.count ? Math.ceil(visits.count / pageSize) - 1 : 0;
    const currentPage = pageIndex + 1;
    const handleSearch = () => {
        setTriggeredSearchQuery(searchQuery); // Set the search query to be used in the API call
        // setSearchQuery(""); // Optionally clear the input field after search
    
    };

    const handleReset = () => {
        handleSearch();
    };
    // if (error) return <Text color="red.500">Error: {error.message}</Text>;
    if (isFetching) return <Spinner size="lg" />;

    return (
        <Box overflowX="auto">
            <Flex
                            mb="6"
                            justifyContent="space-between"
                            alignItems="center"
                            wrap="wrap"
                            p="4"
                            borderWidth="1px"
                            borderColor="teal.200"
                            borderRadius="md"
                            boxShadow="md"
                        >
                            <Flex
                                alignItems="center"
                                flex="1"
                                justifyContent={{ base: "center", md: "flex-start" }} // Center on small screens, left-align on medium and up
                                mt={{ base: 2, md: 0 }} // Margin top on small screens
                            >
                                <InputGroup
                                    width={{ base: "100%", md: "300px" }}
                                    borderWidth="2px"
                                    borderColor="teal.500"
                                    borderRadius="md"
                                    mr={{ base: 0, md: 4 }}
                                >
                                    <InputLeftElement pointerEvents="none">
                                        <Icon as={SearchIcon} color="teal.500" />
                                    </InputLeftElement>
                                    <Input
                                        placeholder="Enter date (YYYY-MM-DD)"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        borderRadius="md"
                                        p="4"
                                        pl="10"
                                        _placeholder={{ color: "teal.300" }}
                                    />
                                </InputGroup>
                                <Button
                                    onClick={handleSearch}
                                    colorScheme="teal"
                                    size="md"
                                    borderRadius="md"
                                    px="6"
                                    mr="2" // Space between search and reset buttons
                                    _hover={{ bg: "teal.600" }}
                                >
                                    Search by Date
                                </Button>
                                <Button
                                    onClick={handleReset}
                                    colorScheme="gray" // A neutral color for the reset action
                                    size="md"
                                    borderRadius="md"
                                    px="6"
                                    _hover={{ bg: "gray.600" }}
                                >
                                    Reset
                                </Button>
                            </Flex>
                            
                        </Flex>
            {triggeredSearchQuery && (
                <Text mb="4" color="teal.500" fontSize="md">
                    Found {visits?.count || 0} results for "{triggeredSearchQuery}"
                </Text>
            )}
            <Table variant="striped" colorScheme="teal">
                <Thead>
                    <Tr>
                        {columns.map((column) => (
                            <Th key={column.id}>{column.header}</Th>
                        ))}
                    </Tr>
                </Thead>
                <Tbody>
                    {visits?.results?.length > 0 ? (
                        visits.results.map((row, i) => (
                            <Tr key={i}>
                                {columns.map((column) => (
                                    <Td key={column.id}>
                                        {typeof column.accessor === "function"
                                            ? column.accessor(row)
                                            : row[column.accessor]}
                                    </Td>
                                ))}
                            </Tr>
                        ))
                    ) : (
                        <Tr>
                            <Td colSpan={columns.length} textAlign="center">
                                No visits available
                            </Td>
                        </Tr>
                    )}
                </Tbody>
            </Table>
            <Flex justifyContent="space-between" alignItems="center" p="4">
                <Button
                    onClick={() => setPageIndex(0)}
                    disabled={pageIndex === 0}
                >
                    {"<<"}
                </Button>
                <Button
                    onClick={() => setPageIndex((old) => Math.max(old - 1, 0))}
                    disabled={pageIndex === 0}
                >
                    {"<"}
                </Button>
                <Text>
                    Page {currentPage} of {lastPage + 1}
                </Text>
                <span>
                    | Go to page:{" "}
                    <Input
                        type="number"
                        value={pageIndex + 1}
                        onChange={(e) => {
                            const page = e.target.value
                                ? Number(e.target.value) - 1
                                : 0;
                            setPageIndex(page);
                        }}
                        style={{ width: "100px" }}
                    />
                </span>
                <Button
                    onClick={() =>
                        setPageIndex((old) => Math.min(old + 1, lastPage))
                    }
                    disabled={pageIndex >= lastPage}
                >
                    {">"}
                </Button>
                <Button
                    onClick={() => setPageIndex(lastPage)}
                    disabled={pageIndex >= lastPage}
                >
                    {">>"}
                </Button>
                <Select
                    ml="4"
                    width="auto"
                    value={pageSize}
                    onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setPageIndex(0); // Reset to the first page after changing page size
                    }}
                >
                    {[10, 25, 50, 100].map((size) => (
                        <option key={size} value={size}>
                            Show {size}
                        </option>
                    ))}
                </Select>
            </Flex>
        </Box>
    );
}

function AllVisits() {
    const { data: visits, error, isLoading } = useGetAllVisitsQuery({
        page: 1,
        pageSize: 10,
    });
    const visitCount = visits ? visits.count : 0;
    const {
        data: currentUser,
        error: err,
        isLoading: isFetching,
    } = useGetCurrentUserQuery();
   
    const navigate = useNavigate();

    const handleDetailsClick = (patientId, visitId) => {
        navigate(`/patients/${patientId}/visits/${visitId}/details`);
    };

    useEffect(() => {
        if (isFetching) {
            console.log("Loading current user data...");
        }

        if (err) {
            console.error("Error fetching current user:", err);
        }

        if (currentUser) {
            console.log("Current User:", currentUser);
            console.log("Current User ID:", currentUser.id);
        }
    }, [currentUser, err, isFetching]);

    // Fetch users data
    const { data: users, isLoading: isLoadingUsers } = useGetUsersQuery();

    const userMap = useMemo(() => {
        const map = {};
        if (users && users.results) {
            // Make sure you access the results array correctly.
            users.results.forEach((user) => {
                if (user.id && user.username) {
                    map[user.id] = user.username;
                }
            });
        }
        return map;
    }, [users]); // Make sure 'users' contains the fetched data array.

    const columns = useMemo(
        () => [
            {
                id: "patient",
                header: "Patient",
                accessor: row => `${row.patient_details?.name || "Unknown"} ${row.patient_details?.surname || "Unknown"}`, 
            },
            {
                id: "date",
                header: "Date of Visit",
                accessor: (row) => moment(row.date).format("MMMM D, YYYY"), // Assuming row.date is the correct path
            },
            {
                id: "time",
                header: "Time of Visit",
                accessor: (row) =>
                    moment(row.time, "HH:mm:ss.SSSSSS").format("hh:mm A"), // Assuming row.time is the correct path
            },
            {
                id: "reason",
                header: "Reason for Visit",
                accessor: (row) => row.reason, // Directly accessing the reason
            },
            {
                id: "registered_by",
                header: "Registered By",
                accessor: (row) => {
                    const username = userMap[String(row.registered_by)]; // Accessing from userMap directly
                    return username || "Unknown User";
                },
            },
            {
                id: "actions",
                header: "Actions",
                accessor: (row) => (
                    <div>
                        <IconButton
                            aria-label="View details"
                            icon={<InfoIcon />}
                            onClick={() => handleDetailsClick(row.patient_details?.id ?? row.patient, row.id)}
                            mr={2}
                        />
                    </div>
                ),
            },
        ],
        [userMap]
    );

    // Chakra Color Mode
    const brandColor = useColorModeValue("brand.500", "white");
    const boxBg = useColorModeValue("secondaryGray.300", "whiteAlpha.100");

    return (
        <>
            <PageTitle title="All Patient Visits" />
            <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
                <SimpleGrid
                    columns={{ base: 1, md: 2, lg: 3, "2xl": 6 }}
                    gap="20px"
                    mb="20px"
                >
                    <MiniStatistics
                        startContent={
                            <IconBox
                                w="56px"
                                h="56px"
                                bg={boxBg}
                                icon={
                                    <Icon
                                        w="32px"
                                        h="32px"
                                        as={MdPerson}
                                        color={brandColor}
                                    />
                                }
                            />
                        }
                        name="Total Visits"
                        value={
                            isLoading ? (
                                <Spinner size="lg" />
                            ) : err ? (
                                <Text>Error fetching visits</Text>
                            ) : (
                                visitCount
                            )
                        }
                    />
                </SimpleGrid>

                <SimpleGrid
                    columns={{ base: 1, md: 1, xl: 1 }}
                    gap="20px"
                    mb="20px"
                >
                    <PaginatedTable columns={columns} initialPageSize={10} />
                </SimpleGrid>
            </Box>
        </>
    );
}

export default AllVisits;
